/**
 * Usage tab — license assignment per plan line + recording storage. Zoom Phone CALL ACTIVITY moved to
 * the Reports tab (owner ruling 2026-09-19: Usage = licences bought vs assigned, Reports = activity over
 * time); the phone LICENCE rows and the phone-not-enabled note stay here.
 *
 * THE PLAN TABLE'S ROWS ARE THE SUBSCRIPTION TAB'S VISIBLE ROWS (N0-3, owner rulings 2026-09-18). The API
 * builds them from the same line set (customers/service.py::_usage_rows over _display_lines), drops the
 * lines the tier bundles, and attaches each line's OWN figures. This component renders them verbatim —
 * no fallback to another figure: a fallback is exactly how the old table paired the Pay-As-You-Go shell
 * with the Pro line's 30 seats. A Free account shows the Basic Plan (Free) card, like the Subscription tab.
 * Recording storage shows Unlimited ONLY when the base tier actually is (free_storage="Unlimited").
 */

import { useTranslation } from "react-i18next";
import { Table, type Column, ErrorState, EmptyState, SkeletonRows } from "@/components/ui";
import { PlanFreezeNotice } from "@/components/portal/PlanFreezeNotice";
import { formatInstant, formatInt } from "@/lib/format";
import { isPlanNotLive } from "@/lib/planState";
import { planName } from "@/lib/planNames";
import {
  Section,
  UtilizationBar,
  utilizationTone,
  KpiTile,
  bandFromPct,
  FeedNote,
} from "../display";
import { useCustomerUsage } from "../useCustomer";

interface PlanRow {
  key: string;
  plan: string;
  assigned: number | null;
  capacity: number | null;
  util: number | null;
}

const num = (v: unknown): number | null => (v == null || v === "" ? null : Number(v));

export function UsageTab({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const usage = useCustomerUsage(accountId, true);

  if (usage.isPending) return <SkeletonRows rows={6} />;
  if (usage.isError) return <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />;

  const {
    plan_usage,
    recording_storage,
    addons,
    base_type,
    synced_at,
    note,
    plan_state,
    plan_badge,
    is_free,
    phone_not_enabled,
  } = usage.data;
  // When the base plan is not live, Zoom's /plans/usage endpoint returns NOTHING for this account — so
  // every figure on this tab (hosts_total/hosts_used, the add-on rows, the utilization bands, the storage
  // cap) is frozen at the moment the trial or the paid term ended and will never refresh. ISALAMIC renders
  // "15 purchased / 1 used" on a plan that died on 2026-05-28. The tab must not present that as current.
  const planFrozen = isPlanNotLive(plan_state);

  // Base row, then the add-on lines — both labelled from the line's OWN SKU with planName(plan_type),
  // which keeps the term (and capacity) in the name because this tab has no Billing Period / Details
  // column (owner ruling 2026-09-19, W4-Q1: keep the term wherever there is no Billing Period column).
  // The Subscription tab deliberately differs: it prints the bare product (lib/zoomLine.ts productName,
  // T3) next to its own Billing Period. W4-Q2 (no "--" prefix / T7 blank here) is still open — see the
  // API repo's docs/ZOOM-DISPLAY-PARITY.md. Every figure is the API's, verbatim: null renders "—".
  const rows: PlanRow[] = [
    ...plan_usage.map((r, i) => ({
      key: `base:${r.plan_category ?? "?"}:${r.plan_type ?? "?"}:${i}`,
      plan: planName(r.plan_type),
      assigned: num(r.hosts_used),
      capacity: num(r.hosts_total),
      util: num(r.utilization_pct),
    })),
    ...addons.map((a, i) => ({
      // Index-suffixed so two lines of the same SKU never collide on the React key.
      key: `addon:${a.plan_category ?? "?"}:${a.plan_type ?? "?"}:${i}`,
      plan: planName(a.plan_type),
      assigned: num(a.assigned),
      // The line's OWN quantity — never a category sum. null for the Zoom Phone shell ("—", as Zoom).
      capacity: num(a.assigned_capacity),
      // null is deliberate (phone; 0 assigned; holders exceed seats) — never recomputed here.
      util: a.is_payg ? null : num(a.assigned_util_pct),
    })),
  ];

  const planCols: Column<PlanRow>[] = [
    {
      key: "plan",
      header: t("customer.usage.plan"),
      cell: (r) => <span className="font-medium text-(--text)">{r.plan}</span>,
      sortValue: (r) => r.plan,
    },
    {
      key: "assigned",
      header: t("customer.usage.assigned"),
      cell: (r) => (r.assigned == null ? "—" : formatInt(r.assigned)),
      sortValue: (r) => r.assigned,
      align: "right",
      numeric: true,
    },
    {
      key: "capacity",
      header: t("customer.usage.capacity"),
      cell: (r) => (r.capacity == null ? "—" : formatInt(r.capacity)),
      sortValue: (r) => r.capacity,
      align: "right",
      numeric: true,
    },
    {
      key: "util",
      header: t("customer.utilization"),
      cell: (r) =>
        r.util == null ? (
          <span className="text-(--text-subtle)">—</span>
        ) : (
          <UtilizationBar
            pct={r.util}
            tone={utilizationTone(bandFromPct(r.util))}
            className="w-40"
          />
        ),
      sortValue: (r) => r.util,
    },
  ];

  // Recording storage: Unlimited only when the base tier truly is; else cap = free + paid plan storage.
  const rec = recording_storage;
  const freeRaw = rec?.free_storage ?? "";
  const unlimited = /unlimited/i.test(freeRaw);
  // Use the unit-corrected GB from the mirror — NOT parseFloat(freeRaw), which drops the unit and reads
  // "21.98 TB" as 22 GB (→ a bogus 20000%+ cap, seen on Chula).
  const freeGb = unlimited ? 0 : (num(rec?.free_storage_gb) ?? 0);
  const planGb = num(rec?.plan_storage_gb) ?? 0;
  const capGb = freeGb + planGb;
  const usedGb = num(rec?.used_storage_gb) ?? 0;
  const pct = !unlimited && capGb > 0 ? (usedGb / capGb) * 100 : null;

  return (
    <div className="flex flex-col gap-6">
      <Section title={t("customer.usage.planUsage")}>
        {/* THE FREEZE NOTICE, ABOVE the numbers it is about. Not decoration: without it this tab renders
            "15 purchased / 1 used" in completely normal, healthy styling for an account Zoom's own console
            reports as Basic with no assignable licences — and a CSM quotes it as live data. */}
        <PlanFreezeNotice plan_state={plan_state} plan_badge={plan_badge} base_type={base_type} />
        {/* ⚠️ THE SUBTLE TRAP. `synced_at` is max(fetched_at) of sub_account_plans, and ZPCD re-fetches
            the plan row EVERY DAY — including for a dead plan, where all it learns is "still expired".
            So this caption reads "Last synced: today" while the figures beside it have been frozen since
            May. That is not a stale caption; it is a FRESH caption over STALE numbers, which is worse —
            it actively certifies the lie. So we suppress it when the plan is frozen.
            This suppression is now the WHOLE of the freshness answer: the freeze notice used to carry a
            "Zoom last refreshed this plan N days ago" trailer, which on these rows always read "0 days
            ago" and flatly contradicted the sentence in front of it. Deleting the trailer loses no fact —
            it removes a claim that was never useful. What matters is that nothing on this tab CERTIFIES
            the frozen figures as fresh, and that still holds. */}
        {!planFrozen && synced_at && (
          <p className="text-(length:--text-xs) text-(--text-muted)">
            {t("syncNow.lastSynced", { time: formatInstant(synced_at) })}
          </p>
        )}
        {is_free ? (
          // Same card as the Subscription tab: Zoom shows a Free account no plan table at all, even when
          // the mirror still holds rows from a plan that ended.
          <EmptyState
            title={t("customer.subscription.freePlanTitle")}
            body={t("customer.subscription.freePlanBody")}
          />
        ) : (
          <Table
            columns={planCols}
            rows={rows}
            rowKey={(r) => r.key}
            caption={t("customer.usage.planUsage")}
            initialSort={{ key: "util", dir: "desc" }}
            pinFirst={(r) => r.key.startsWith("base:")}
            paginated={false}
            empty={<EmptyState title={t("customer.usage.noPlanUsage")} />}
          />
        )}
        {/* Owner ruling 2026-09-18: the purchased Zoom Phone line reads 0 Assigned because Zoom refused
            the phone calls with 403/2031 — say why, beside the 0. */}
        {phone_not_enabled && !is_free && (
          <FeedNote>{t("customer.usage.phoneNotEnabled")}</FeedNote>
        )}
        {note && !is_free && <FeedNote>{note}</FeedNote>}
      </Section>

      <Section title={t("customer.usage.recordingStorage")}>
        {rec ? (
          // r10 22/7: auto-fit KPI tiles — no fixed column cap now the shell is full-bleed.
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
            <KpiTile
              label={t("customer.usage.usedStorage")}
              value={`${formatInt(usedGb)} GB`}
              {...(pct == null
                ? {}
                : {
                    sub: (
                      <UtilizationBar
                        pct={pct}
                        tone={utilizationTone(bandFromPct(pct))}
                        className="mt-1 w-40"
                      />
                    ),
                  })}
            />
            <KpiTile
              label={t("customer.usage.planStorage")}
              mono={!unlimited}
              value={
                unlimited
                  ? t("customer.usage.unlimited")
                  : capGb > 0
                    ? `${formatInt(capGb)} GB`
                    : "—"
              }
            />
          </div>
        ) : (
          <EmptyState title={t("customer.usage.noStorage")} />
        )}
      </Section>
    </div>
  );
}
